// External Dependencies
import React, { useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardBody, Flex, useDisclosure } from '@chakra-ui/react';
import {
  DisplayAggregateQuestionDto,
  DisplayIndividualQuestionDto,
  QuestionVisualizationEnum,
} from '@keyops-cep/api-client';

import useFilterProvider from '../../HOCs/Filter/useFilterProvider';
// Internal Dependencies
import {
  AnswerDataWithDemographic,
  MaxDiffAnswerDataWithDemographic,
} from '../../pages/EngagementDetails/types';

import ChoiceDistributionMaxDiff from './MaxDiff/ChoiceDistributionMaxDiff';
import AverageRankForEach from './AverageRankForEach';
import Distribution from './Distribution';
import Histogram from './Histogram';
import HorizontalBarGraph from './HorizontalBarGraph';
import HorizontalHistogram from './HorizontalHistogram';
import ListOfResponses from './ListOfResponses';
import Matrix from './Matrix';
import MaxDiff from './MaxDiff';
import MultiSelectGrid from './MultiSelectGrid';
import { NoResultVisualization } from './NoResultVisualization';
import { SharedExpandedViewModal } from './SharedExpandedViewModal';
import StackedBarChart from './StackedBarChart';
import TextGrid from './TextGrid';
import VisualizationCardOptions from './VisualizationCardOptions';

type VisualizationProps = {
  question: DisplayIndividualQuestionDto | DisplayAggregateQuestionDto;
  questionAnswers: AnswerDataWithDemographic[];
  displayPrevAvg?: boolean;
  togglePrevAvgDisplay?: () => void;
  displayDataTable?: boolean;
  toggleDataTableDisplay?: () => void;
  displayEditToggle?: boolean;
  toggleEditDisplay?: () => void;
  hideFilterBar?: boolean;
  height?: string;
  useQuestionComparativeModal: boolean;
};

export interface VisualizationChartRef {
  onDownload: (name: string) => void;
  onCopyToClipboard: () => void;
}

export function getVisualizationComponent(
  question: DisplayIndividualQuestionDto | DisplayAggregateQuestionDto,
  questionAnswers: AnswerDataWithDemographic[],
  ref: React.RefObject<VisualizationChartRef>,
  displayDataTable?: boolean,
  maintainAspectRatio?: boolean,
  isExpanded?: boolean,
  displayPrevAvg?: boolean
): { component: JSX.Element } {
  let VisualizationComponent = <></>;

  // When the selected filters have no related questionAnswers
  if (questionAnswers.length === 0) {
    return {
      component: <NoResultVisualization />,
    };
  }

  // Return Matrix visualization when displayDataTable is true
  if (displayDataTable) {
    if (question.visualization === 'MaxDiff') {
      return {
        component: (
          <ChoiceDistributionMaxDiff
            ref={ref}
            question={question as DisplayAggregateQuestionDto}
            questionAnswers={
              questionAnswers as MaxDiffAnswerDataWithDemographic[]
            }
          />
        ),
      };
    }
    return {
      component: (
        <Matrix
          ref={ref}
          question={question as DisplayAggregateQuestionDto}
          questionAnswers={questionAnswers}
        />
      ),
    };
  }

  // TODO: update the visualizations for StackedBarChart and MultiSelectGrid
  switch (question.visualization as string) {
    case QuestionVisualizationEnum.MaxDiff:
      VisualizationComponent = (
        <MaxDiff
          ref={ref}
          question={question as DisplayAggregateQuestionDto}
          questionAnswers={
            questionAnswers as MaxDiffAnswerDataWithDemographic[]
          }
          maintainAspectRatio={maintainAspectRatio}
          isExpanded={isExpanded}
        />
      );
      break;
    case QuestionVisualizationEnum.Matrix:
      VisualizationComponent = (
        <Matrix
          ref={ref}
          question={question as DisplayAggregateQuestionDto}
          questionAnswers={questionAnswers}
        />
      );
      break;
    // Matrix single select
    case QuestionVisualizationEnum.StackedBarChart:
      VisualizationComponent = (
        <StackedBarChart
          ref={ref}
          question={question as DisplayAggregateQuestionDto}
          questionAnswers={questionAnswers}
          maintainAspectRatio={maintainAspectRatio}
          isExpanded={isExpanded}
        />
      );
      break;
    // Matrix dropdown select
    case QuestionVisualizationEnum.MultiSelectGrid:
      VisualizationComponent = (
        <MultiSelectGrid
          ref={ref}
          question={question as DisplayAggregateQuestionDto}
          questionAnswers={questionAnswers}
        />
      );
      break;
    case QuestionVisualizationEnum.Histogram:
      VisualizationComponent = (
        <Histogram
          ref={ref}
          question={question as DisplayIndividualQuestionDto}
          questionAnswers={questionAnswers}
          displayPrevAvg={displayPrevAvg}
          maintainAspectRatio={maintainAspectRatio}
          isExpanded={isExpanded}
        />
      );
      break;
    case QuestionVisualizationEnum.HorizontalHistogram:
      VisualizationComponent = (
        <HorizontalHistogram
          ref={ref}
          question={question as DisplayAggregateQuestionDto}
          questionAnswers={questionAnswers}
          maintainAspectRatio={maintainAspectRatio}
          isExpanded={isExpanded}
        />
      );
      break;
    case QuestionVisualizationEnum.Distribution:
      VisualizationComponent = (
        <Distribution
          ref={ref}
          question={question as DisplayIndividualQuestionDto}
          questionAnswers={questionAnswers}
          displayPrevAvg={displayPrevAvg}
          maintainAspectRatio={maintainAspectRatio}
          isExpanded={isExpanded}
        />
      );
      break;
    case QuestionVisualizationEnum.HorizontalBarGraphOfAverages:
      VisualizationComponent = (
        <HorizontalBarGraph
          ref={ref}
          question={question as DisplayAggregateQuestionDto}
          questionAnswers={questionAnswers}
          maintainAspectRatio={maintainAspectRatio}
          isExpanded={isExpanded}
        />
      );
      break;
    case QuestionVisualizationEnum.ListOfResponses:
      VisualizationComponent = (
        <ListOfResponses ref={ref} questionAnswers={questionAnswers} />
      );
      break;
    case QuestionVisualizationEnum.TextGrid:
      VisualizationComponent = (
        <TextGrid
          ref={ref}
          question={question as DisplayAggregateQuestionDto}
          questionAnswers={questionAnswers}
        />
      );
      break;
    case QuestionVisualizationEnum.AverageRankForEach:
      VisualizationComponent = (
        <AverageRankForEach ref={ref} questionAnswers={questionAnswers} />
      );
      break;
    default:
      VisualizationComponent = (
        <ListOfResponses questionAnswers={questionAnswers} />
      );
      break;
  }
  return {
    component: VisualizationComponent,
  };
}

const Visualization = ({
  question,
  questionAnswers,
  displayPrevAvg,
  togglePrevAvgDisplay,
  displayDataTable,
  toggleDataTableDisplay,
  displayEditToggle,
  toggleEditDisplay,
  hideFilterBar,
  useQuestionComparativeModal,
  height = '100%',
}: VisualizationProps): JSX.Element => {
  const cardVisualizationRef = useRef<VisualizationChartRef>(null);

  // Handle SharedExpandedViewModal
  const {
    isOpen: isSharedExpandedViewModalOpen,
    onOpen: openSharedExpandedViewModal,
    onClose: closeSharedExpandedViewModal,
  } = useDisclosure();

  const { setBreakoutDemographic } = useFilterProvider();

  // Handle navigation to QuestionResultsModal
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const openQuestionResultsModalWithURL = (id: string) => {
    navigate({ search: searchParams.toString(), hash: `expandedq${id}` });
  };

  // In the context of the surveys result page, onExpand opens QuestionResultsModal by adding a hash `#expandedq${QuestionId}`
  // In the context of the Comparative analysis of 2 questions, onExpand directly opens SharedExpandedViewModal
  const handleOpenModal = () => {
    useQuestionComparativeModal
      ? openSharedExpandedViewModal()
      : openQuestionResultsModalWithURL(question.id);
  };

  const onSharedExpandedViewModalClose = () => {
    closeSharedExpandedViewModal();
    setBreakoutDemographic(undefined);
  };

  const { component: CardVisualizationComponent } = getVisualizationComponent(
    question,
    questionAnswers,
    cardVisualizationRef,
    displayDataTable,
    false,
    false,
    displayPrevAvg
  );

  return (
    <>
      <Flex h={height}>
        <Card flexGrow={1}>
          <CardBody overflowY={'hidden'}>{CardVisualizationComponent}</CardBody>
        </Card>
        <VisualizationCardOptions
          visualizationRef={cardVisualizationRef}
          question={question}
          onExpand={handleOpenModal}
          displayDataTable={displayDataTable}
          toggleDataTableDisplay={toggleDataTableDisplay}
          displayEditToggle={displayEditToggle}
          toggleEditDisplay={toggleEditDisplay}
        />
      </Flex>

      {/* Will be opened only in the Question comparison page context */}
      {useQuestionComparativeModal && (
        <SharedExpandedViewModal
          isOpen={isSharedExpandedViewModalOpen}
          onClose={onSharedExpandedViewModalClose}
          currentQuestion={question}
          currentQuestionAnswers={questionAnswers}
          displayPrevAvg={displayPrevAvg}
          togglePrevAvgDisplay={togglePrevAvgDisplay}
          hideFilterBar={hideFilterBar}
          isControlled={useQuestionComparativeModal}
          displayDataTable={displayDataTable}
          toggleDataTableDisplay={toggleDataTableDisplay}
          displayEditToggle={displayEditToggle}
          toggleEditDisplay={toggleEditDisplay}
        />
      )}
    </>
  );
};

export default Visualization;
